/**
 * Projects — Table view for project management
 *
 * Table columns: Star, Name+Description, Members, Chats, Files, Last Activity, Created, Actions
 * Click row → navigate to project chat
 * Settings icon → navigate to full-page settings (project detail)
 * New project → create modal
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Plus, Search, Star, Settings, Trash2,
  ArrowLeft, MoreVertical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/use-has-permission';
import { toast } from 'sonner';

export const Route = createFileRoute('/ask-argus/projects')({
  component: ProjectsPage,
});

// ── Mock Data ───────────────────────────────────────────────────────────────

interface Project {
  id: string;
  name: string;
  description: string;
  color: string;
  starred: boolean;
  conversationCount: number;
  fileCount: number;
  members: { name: string; initials: string }[];
  lastActivity: string;
  createdAt: string;
  createdBy: string;
}

// Real data hook + adapter
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ServerProject {
  id: string; name: string; description: string; color: string;
  starred: boolean; conversationCount: number; fileCount: number;
  createdAt: string; createdBy: string | null; lastActivityAt: string;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function adaptProject(s: ServerProject): Project {
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    color: s.color,
    starred: s.starred,
    conversationCount: s.conversationCount,
    fileCount: s.fileCount,
    members: [],
    lastActivity: timeAgo(s.lastActivityAt),
    createdAt: s.createdAt.slice(0, 10),
    createdBy: s.createdBy ?? '—',
  };
}

const SHARED_PROJECTS: Project[] = [];

const COLOR_PRESETS = ['#8b5cf6', '#6366f1', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

// ── Component ───────────────────────────────────────────────────────────────

function ProjectsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  // P2 review BF-1 / BF-2 — affordance gates matching the API. POST
  // /ai-projects now requires `write:projects` and DELETE /ai-projects/:id
  // requires `delete:projects` (routes/v1/projects.ts), so a role that would
  // be refused must not be shown the control. Affordance only: the API is
  // the barrier.
  const canCreateProject = useHasPermission('write', 'projects');
  const canDeleteProject = useHasPermission('delete', 'projects');

  // Real-data: projects table with per-user starred flag.
  const { data: serverProjects = [] } = useQuery<{ projects: ServerProject[] }, Error, Project[]>({
    queryKey: ['ai-projects'],
    queryFn: () => apiClient.get<{ projects: ServerProject[] }>('/ai-projects'),
    select: (r) => r.projects.map(adaptProject),
  });

  // #566 — persist the new project via POST /ai-projects. The previous
  // wiring fired a fake `toast.success` and closed the dialog without
  // touching the backend, so the new row never appeared in the list
  // and `GET /api/v1/ai-projects` returned an empty list.
  type CreateProjectBody = { name: string; description: string; color: string; instructions?: string };
  const createMutation = useMutation<ServerProject, Error, CreateProjectBody>({
    mutationFn: (body: CreateProjectBody) =>
      apiClient.post<ServerProject>('/ai-projects', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-projects'] });
    },
  });
  // #4 — real project deletion (the dropdown "Delete" previously only fired
  // `toast.info('Delete project')` with no backend call). Controlled by a
  // confirm dialog so a destructive action isn't a single mis-click.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (id: string) => apiClient.delete(`/ai-projects/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-projects'] });
      toast.success('Project deleted');
      setDeleteTarget(null);
    },
    onError: (e) => toast.error('Delete failed', { description: e.message }),
  });

  const MOCK_PROJECTS = serverProjects;
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set());
  // Initialize starred state from server
  useEffect(() => {
    setStarredIds(new Set(serverProjects.filter(p => p.starred).map(p => p.id)));
  }, [serverProjects]);

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newColor, setNewColor] = useState(COLOR_PRESETS[0]);
  const [newInstructions, setNewInstructions] = useState('');

  const allProjects = [...MOCK_PROJECTS, ...SHARED_PROJECTS];
  const filtered = allProjects.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted, sortConfig, handleSort } = useTableSort<Project>(filtered, 'name', 'asc');

  const handleCreate = () => {
    const trimmedName = newName.trim();
    if (!trimmedName) return;
    createMutation.mutate(
      {
        name: trimmedName,
        description: newDescription.trim(),
        color: newColor,
        instructions: newInstructions.trim() || undefined,
      },
      {
        onSuccess: (row: ServerProject) => {
          toast.success(`Project "${row.name}" created`);
          setCreateOpen(false);
          setNewName('');
          setNewDescription('');
          setNewColor(COLOR_PRESETS[0]);
          setNewInstructions('');
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Could not create project');
        },
      },
    );
  };

  const toggleStar = (id: string) => {
    setStarredIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="h-full overflow-auto">
      {/* Header */}
      <div className="border-b border-border/40 bg-background shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: '/ask-argus' })} aria-label="Back to chat" title="Back">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-semibold">Projects</h1>
            </div>
            {canCreateProject && (
              <Button onClick={() => setCreateOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" /> New project
              </Button>
            )}
          </div>

          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search projects..." value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} className="pl-9" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {sorted.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">{search ? 'No projects match your search' : 'No projects yet'}</p>
            {canCreateProject && (
              <Button onClick={() => setCreateOpen(true)} className="mt-4 gap-2"><Plus className="h-4 w-4" /> Create project</Button>
            )}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="w-10 px-3 py-2.5"></th>
                  <th className="text-left px-4 py-2.5 font-medium"><SortableHeader label="Project" sortKey="name" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="text-left px-4 py-2.5 font-medium"><SortableHeader label="Members" sortKey="members.length" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="text-center px-3 py-2.5 font-medium"><SortableHeader label="Chats" sortKey="conversationCount" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="text-center px-3 py-2.5 font-medium"><SortableHeader label="Files" sortKey="fileCount" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="text-left px-4 py-2.5 font-medium"><SortableHeader label="Created By" sortKey="createdBy" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="text-left px-4 py-2.5 font-medium"><SortableHeader label="Last Active" sortKey="lastActivity" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="text-left px-4 py-2.5 font-medium"><SortableHeader label="Created" sortKey="createdAt" currentSort={sortConfig} onSort={handleSort} /></th>
                  <th className="w-24 px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {sorted.map(project => (
                  <tr
                    key={project.id}
                    className="hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate({ to: '/ask-argus/project/$projectId', params: { projectId: project.id }, search: { view: '' } })}
                  >
                    <td className="px-3 py-3">
                      <button
                        className={cn('h-6 w-6 flex items-center justify-center rounded', starredIds.has(project.id) ? 'text-yellow-500' : 'text-muted-foreground/30 hover:text-muted-foreground')}
                        onClick={(e) => { e.stopPropagation(); toggleStar(project.id); }}
                      >
                        <Star className={cn('h-3.5 w-3.5', starredIds.has(project.id) && 'fill-yellow-500')} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{project.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[300px]">{project.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center -space-x-1">
                        {project.members.slice(0, 3).map((m, i) => (
                          <div key={i} className="w-6 h-6 rounded-full bg-muted border-2 border-card flex items-center justify-center text-[8px] font-semibold text-muted-foreground" title={m.name}>
                            {m.initials}
                          </div>
                        ))}
                        {project.members.length > 3 && (
                          <div className="w-6 h-6 rounded-full bg-muted border-2 border-card flex items-center justify-center text-[8px] font-semibold text-muted-foreground">+{project.members.length - 3}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-muted-foreground">{project.conversationCount}</td>
                    <td className="px-3 py-3 text-center text-muted-foreground">{project.fileCount}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{project.createdBy}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{project.lastActivity}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{project.createdAt}</td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate({ to: '/ask-argus/project/$projectId', params: { projectId: project.id }, search: { view: 'settings' } })} title="Settings" aria-label={`Project settings for '${project.name}'`}>
                          <Settings className="h-4 w-4" />
                        </Button>
                        {/* Delete is the menu's only item, so without
                            `delete:projects` the trigger itself would open an
                            empty menu — hide the whole control instead. */}
                        {canDeleteProject && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Project actions for '${project.name}'`} title="More actions"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44 z-50">
                            <DropdownMenuItem className="gap-2 text-destructive" onClick={() => setDeleteTarget({ id: project.id, name: project.name })}><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create project</DialogTitle>
            <DialogDescription>Projects group conversations with shared instructions and knowledge files.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              {/* fix-wave-4 #979 — explicit `type=text`, `aria-label` and
                  `data-testid` so the input is reachable both by the
                  Playwright `role=dialog input[type=text]` selector AND
                  by screen readers. Without `type` the underlying
                  Input forwarded `undefined`, which renders as an
                  attribute-less <input> and matches neither the a11y
                  query nor the matrix's selector. */}
              <Label htmlFor="ask-argus-new-project-name">Project name</Label>
              <Input
                id="ask-argus-new-project-name"
                type="text"
                aria-label="Project name"
                data-testid="ask-argus-new-project-name"
                placeholder="e.g., Q2 Turnaround Planning"
                value={newName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea placeholder="Brief description..." value={newDescription} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewDescription(e.target.value)} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-2">
                {COLOR_PRESETS.map(color => (
                  <button key={color} onClick={() => setNewColor(color)} className={cn('w-7 h-7 rounded-full transition-all', newColor === color ? 'ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110' : 'hover:scale-105')} style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Custom instructions <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea placeholder="Instructions for every conversation in this project..." value={newInstructions} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewInstructions(e.target.value)} rows={3} className="font-mono text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newName.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes “{deleteTarget?.name}”. Its conversations are kept and moved out of the project. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => { e.preventDefault(); if (deleteTarget) deleteMutation.mutate(deleteTarget.id); }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete project'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
